// Stable, opaque keys for form-markup fields.
//
// A field's key is its IDENTITY: it is what ties an inspector's saved answer
// back to the field on the template. It used to be the name the Super Admin
// typed, which made identity and label the same string — so renaming a field
// silently orphaned every answer already saved under the old name, and any
// generic transformation of the JSON (see utils/opaqueKeys.ts) renamed the
// field out from under its own data.
//
// New fields get a key that means nothing and never changes. The typed name
// lives on as a `label` property, free to be edited.
//
// Existing records are deliberately untouched. Their keys happen to look like
// labels, but nothing derives them from the label any more, so they are just
// opaque strings too and keep working exactly as before.

const PREFIX = 'fld_';

// Not a UUID: this only has to be unique within one page of one form, and a
// short key keeps the stored markup readable when debugging a record.
const token = (): string => Math.random().toString(36).slice(2, 8);

/** True for keys this module generated. Old, label-derived keys return false. */
export const isGeneratedKey = (key: string): boolean =>
  typeof key === 'string' && key.startsWith(PREFIX);

/**
 * A key that is not in use on this page. `taken` is whatever the page already
 * holds — including old label-shaped keys, which must never be collided with.
 */
export const newFieldKey = (taken: Iterable<string> = []): string => {
  const used = new Set(taken);
  // Bounded rather than `while (true)`: 36^6 is ~2 billion, so a collision is
  // already vanishingly unlikely, and a runaway loop in the builder is worse
  // than an astronomically improbable duplicate.
  for (let i = 0; i < 50; i += 1) {
    const key = `${PREFIX}${token()}`;
    if (!used.has(key)) return key;
  }
  return `${PREFIX}${token()}${used.size}`;
};
